import { useState } from 'react';

import { StatementSummary, truncateStatementText } from './StatementSummary';
import { pickCitationSearchLabels } from '../../model/citationSearchProgress';
import type { ExtractedStatement } from '../../model/extractedStatements';

import { HStack } from '@/components/ui/hstack';
import { Progress, ProgressFilledTrack } from '@/components/ui/progress';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import useProgressiveLoading from '@/hooks/useProgressiveLoading';

interface SearchingStatementRowProps {
  statement: ExtractedStatement;
  statementIndex: number;
  /**
   * True once the backend has reported the search complete. Flips the
   * bar to a full 100 % fill; until then the bar approaches — but never
   * reaches — `FILL_CEILING`. Shared across every row in the list so the
   * bars snap to full in lockstep at the real completion signal.
   */
  isComplete: boolean;
}

/**
 * Ceiling the pending fill approaches asymptotically. Search duration is
 * unpredictable (the backend may finish in seconds or tens of seconds),
 * so the bar can't honestly pace to a known end. It fills fast then
 * decelerates toward this ceiling and holds just short of full until the
 * real completion signal snaps it to 100 % — the classic unknown-duration
 * progress pattern.
 */
const FILL_CEILING = 0.9;

/**
 * Time constant (τ) of the asymptotic fill in milliseconds: the bar
 * covers ~63 % of the remaining distance to `FILL_CEILING` every τ. Tuned
 * so the fill reads as brisk early motion that eases off as the search
 * runs long.
 */
const FILL_TIME_CONSTANT_MS = 8_000;

/** Fixed cadence for the rotating stage copy, independent of search
 * duration now that no expected duration is known. */
const PHRASE_INTERVAL_MS = 5_000;

/**
 * Per-statement progress row rendered in the triage sheet's searching
 * phase. Three pieces of UI:
 *
 *   1. The statement excerpt (matches the selection-phase row layout).
 *   2. A `Progress` bar that fills fast-then-decelerating toward
 *      `FILL_CEILING`, driven by `elapsedMs` from `useProgressiveLoading`
 *      so the fill is **monotonically increasing** — the bar never
 *      retreats, never loops, never resets while the row is mounted — and
 *      snaps to a full 100 % when `isComplete` flips on the real backend
 *      completion signal.
 *   3. The current stage copy. The message rotation walks once through
 *      the chosen label sequence and then sticks on the final entry —
 *      `stickyTailCount: 1` prevents the rotation from cycling back
 *      among the trailing messages. Each row assembles its own
 *      sequence via `pickCitationSearchLabels` on mount (one random
 *      line per stage) so voicing varies across rows while staying
 *      stable per row.
 *
 * Rows fill their bars in lockstep — the per-row stagger lives on
 * the list-level entrance animation so the wall builds up visually
 * without breaking the "bar at 100 % before transition" rule (now
 * driven by the shared `isComplete` signal rather than a wall-clock
 * guess).
 */
export function SearchingStatementRow({
  statement,
  statementIndex,
  isComplete,
}: SearchingStatementRowProps) {
  const [progressLabels] = useState(pickCitationSearchLabels);

  const { message, elapsedMs, messageIndex } = useProgressiveLoading(true, {
    messages: progressLabels,
    stickyTailCount: 1,
    delayMs: 0,
    intervalMs: PHRASE_INTERVAL_MS,
  });

  /**
   * Asymptotic fill: fast at first, decelerating toward `FILL_CEILING`,
   * never completing on wall-clock alone. All rows read the same
   * `isComplete` flag, so they snap to a full 100 % together the instant
   * the backend reports the search done — preserving the "bar at 100 %
   * before transition" rule without pacing to a fabricated duration.
   */
  const asymptotic = FILL_CEILING * (1 - Math.exp(-elapsedMs / FILL_TIME_CONSTANT_MS));

  const fillRatio = isComplete ? 1 : asymptotic;

  const fillPercent = Math.round(fillRatio * 100);

  const displayMessage = message ?? progressLabels[0];

  /**
   * 1-based step number rendered as "Step N of M" alongside the rotating
   * copy so the user can gauge how much of the search remains. Falls
   * back to step 1 before the initial delay elapses (when messageIndex
   * is still null) so the counter doesn't blink in late.
   */
  const totalSteps = progressLabels.length;

  const currentStep = (messageIndex ?? 0) + 1;

  const accessibilityLabel = `Statement ${statementIndex + 1}: ${truncateStatementText(
    statement.directStatement
  )}. Step ${currentStep} of ${totalSteps}. ${displayMessage}`;

  return (
    <VStack
      className="w-full rounded-md border border-outline-100 bg-background-0 p-3"
      space="xs"
      accessibilityLabel={accessibilityLabel}
      accessibilityLiveRegion="polite">
      <StatementSummary statement={statement} />
      <Progress value={fillPercent} size="sm" className="mt-1">
        <ProgressFilledTrack />
      </Progress>
      <HStack className="items-center justify-between gap-2">
        <Text size="xs" className="min-w-0 flex-1 italic text-typography-500">
          {displayMessage}
        </Text>
        <Text size="xs" className="shrink-0 font-medium text-typography-500">
          Step {currentStep} of {totalSteps}
        </Text>
      </HStack>
    </VStack>
  );
}
